import { useState, type FormEvent } from 'react';
import {
  SHIFT_REASONS,
  ShiftReasonRequiredError,
  loadJobCalendar,
  listScheduleItems,
  previewShift,
  proposalChanged,
  shiftScheduleItems,
  systemClock,
  type ShiftProposal,
  type ShiftReason,
} from '@buildmate/core';
import { Button, Field, Select } from '@buildmate/ui-web';
import { usePorts } from '../lib/ports-context.tsx';
import { useSession } from '../lib/session-context.tsx';
import { SHIFT_REASON_LABEL } from '../lib/shift-reasons.ts';

/**
 * Agreeing to a change the assistant proposed (SCRUM-223, Phase B).
 *
 * The assistant cannot write. It produces a **proposal** — computed by the
 * same two functions the write uses, so the preview and the result cannot
 * drift — and this is the only thing that turns one into a change. Nothing
 * here is reachable from a tool result: the model can put a proposal in
 * front of somebody and has no way to press the button.
 *
 * **Confirm means confirm _that_, not confirm the request.** A proposal is
 * a screenshot of the schedule at one moment and `shiftScheduleItems` reads
 * the schedule again when it writes, so between the proposal and the press
 * anybody may have dragged a bar, linked a dependency or finished a task.
 * So the preview is **recomputed here** and compared with what was shown;
 * if it differs, nothing is written and the new one is put up instead. A
 * recompute that *throws* — a deletion has left a dangling dependency —
 * refuses for the same reason: a write against a schedule that cannot be
 * read is not a write anybody agreed to.
 *
 * The reason picker appears **only when the write asks for one**, after a
 * `ShiftReasonRequiredError` rather than up front. The bulk dialog on the
 * schedule page asks first because it is already a deliberate act with a
 * form around it; here the common case is an unpublished schedule, and a
 * mandatory dropdown in front of every offer would make the simple answer
 * two clicks and a decision.
 */
export interface AssistantProposalProps {
  readonly proposal: ShiftProposal;
  readonly onDone: (outcome: string) => void;
  readonly onDismiss: () => void;
}

/** The asked-for changes, then the knock-on ones, each capped. */
const SHOWN = 6;

export function AssistantProposal({ proposal, onDone, onDismiss }: AssistantProposalProps) {
  const ports = usePorts();
  const { membership, user } = useSession();
  const [current, setCurrent] = useState(proposal);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsReason, setNeedsReason] = useState(false);
  const [reason, setReason] = useState<ShiftReason>('other');
  const [note, setNote] = useState('');

  const asked = current.changes.filter((change) => !change.knockOn);
  const knockOn = current.changes.filter((change) => change.knockOn);

  const context = {
    data: ports.data,
    clock: systemClock,
    companyId: membership.companyId,
    jobId: current.jobId,
    uid: user.uid,
  };

  async function confirm(event?: FormEvent) {
    event?.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);

    try {
      // Recomputed from a fresh read, against the same calendar the write
      // will use. This is the whole safety property; see the note above.
      const [items, calendar] = await Promise.all([
        listScheduleItems(context),
        loadJobCalendar(context),
      ]);
      let now: ShiftProposal;
      try {
        now = previewShift(calendar, items, current.jobId, current.itemIds, current.byWorkingDays);
      } catch {
        setError(
          'The schedule has changed since this was worked out, and it can no longer be read the same way. Nothing was changed — ask again.',
        );
        return;
      }

      if (proposalChanged(current, now)) {
        setCurrent(now);
        setNeedsReason(false);
        setError(
          now.changes.length === 0
            ? 'The schedule has changed since this was worked out, and nothing would move any more. Nothing was changed.'
            : 'The schedule has changed since this was worked out. Nothing was changed — this is what it would do now.',
        );
        return;
      }

      await shiftScheduleItems(
        context,
        current.itemIds,
        current.byWorkingDays,
        // Only once asked for. An unpublished schedule records the move
        // without a reason, exactly as dragging a bar does.
        needsReason ? { reason, ...(note.trim() ? { note: note.trim() } : {}) } : {},
      );
      /**
       * Reported from the proposal, not from the result.
       *
       * `BulkResult.changed` counts the items the action *named* and
       * `shifts` counts every date that moved, so subtracting one from the
       * other goes negative the moment a named item was already where it
       * was asked to go. And the proposal has just been proved to match
       * what happened, which is what makes quoting it honest.
       */
      onDone(
        knockOn.length > 0
          ? `Moved ${asked.length} ${asked.length === 1 ? 'activity' : 'activities'}, and ${knockOn.length} more followed.`
          : `Moved ${asked.length} ${asked.length === 1 ? 'activity' : 'activities'}.`,
      );
    } catch (cause) {
      if (cause instanceof ShiftReasonRequiredError) {
        // Not an error so much as the next question. Somebody outside the
        // company is planning around these dates.
        setNeedsReason(true);
        setError(null);
        return;
      }
      console.error('[buildmate] confirming an assistant proposal failed', cause);
      setError(
        cause instanceof Error && cause.message
          ? `${cause.message} Nothing was changed.`
          : 'That could not be done. Nothing was changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="A change to confirm"
      className="rounded-lg bg-amber-50 p-3 ring-1 ring-amber-200"
    >
      <p className="text-sm font-medium text-amber-900">{current.summary}</p>

      {current.changes.length > 0 && (
        <>
          <ul className="mt-2 flex flex-col gap-0.5 text-xs text-amber-900">
            {asked.slice(0, SHOWN).map((change) => (
              <li key={change.scheduleItemId}>
                <span className="font-medium">{change.title}</span> {change.from.startDate} →{' '}
                {change.to.startDate}
              </li>
            ))}
            {asked.length > SHOWN && <li>and {asked.length - SHOWN} more</li>}
          </ul>

          {knockOn.length > 0 && (
            /* Separately, and said as a consequence rather than a change:
               this is the part nobody asked for and most needs to see. */
            <p className="mt-2 text-xs text-amber-800">
              {knockOn.length} {knockOn.length === 1 ? 'activity moves' : 'activities move'} because{' '}
              {knockOn.length === 1 ? 'it follows' : 'they follow'} on:{' '}
              {knockOn
                .slice(0, SHOWN)
                .map((change) => change.title)
                .join(', ')}
              {knockOn.length > SHOWN ? `, and ${knockOn.length - SHOWN} more` : ''}
            </p>
          )}
        </>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded-md bg-white px-2 py-1.5 text-xs text-red-700">
          {error}
        </p>
      )}

      <form onSubmit={confirm} className="mt-3">
        {needsReason && (
          <div className="mb-3 flex flex-col gap-2">
            <p className="text-xs text-amber-900">
              These dates are published, so every move records why.
            </p>
            <Select
              label="Reason"
              value={reason}
              onChange={(changeEvent) => setReason(changeEvent.target.value as ShiftReason)}
              options={SHIFT_REASONS.map((value) => ({
                value,
                label: SHIFT_REASON_LABEL[value],
              }))}
            />
            <Field
              label="Note"
              hint="Optional."
              value={note}
              onChange={(changeEvent) => setNote(changeEvent.target.value)}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button type="submit" size="sm" loading={busy} disabled={current.changes.length === 0}>
            {needsReason ? 'Move them' : 'Make this change'}
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={onDismiss} disabled={busy}>
            {/* "Leave it" rather than "Cancel": nothing is in progress to
                cancel, and a proposal declined is a schedule untouched. */}
            Leave it
          </Button>
        </div>
      </form>
    </section>
  );
}
